/**
 * Service Registry for consolidated WebSocket system
 * Manages WebSocket services with dependency injection and lifecycle management
 */

import { AppDispatch } from '@/store/index';
import { IWebSocketService, MessageHandler } from './types';
import { WebSocketMessageRouter } from './messageRouter';

export class WebSocketServiceRegistry {
  private services: Map<string, IWebSocketService> = new Map();
  private handlers: Map<string, MessageHandler[]> = new Map();
  private initialized = false;
  private router: WebSocketMessageRouter | null = null;
  
  // Register a service with dependency checking
  async register(service: IWebSocketService): Promise<void> {
    if (this.initialized) {
      console.warn(`⚠️ Cannot register service ${service.name} after initialization`);
      return;
    }
    
    // Check dependencies
    await this.checkDependencies(service);
    
    // Register service
    this.services.set(service.name, service);
    
    console.log(`✅ Registered WebSocket service: ${service.name} v${service.version}`);
    
    // Log service information
    const info = service.getServiceInfo();
    console.log(`📋 Service ${service.name} info:`, {
      description: info.description,
      supportedMessageTypes: info.supportedMessageTypes,
      dependencies: info.dependencies
    });
  }
  
  // Unregister a service
  async unregister(serviceName: string): Promise<void> {
    const service = this.services.get(serviceName);
    if (!service) {
      console.warn(`⚠️ Service ${serviceName} not found for unregistration`);
      return;
    }
    
    // Remove message handlers
    service.getMessageTypes().forEach(messageType => {
      if (this.handlers.has(messageType)) {
        const handlers = this.handlers.get(messageType)!;
        const filtered = handlers.filter(h => h.service !== serviceName);
        
        if (filtered.length === 0) {
          this.handlers.delete(messageType);
        } else {
          this.handlers.set(messageType, filtered);
        }
      }
    });
    
    // Cleanup service
    try {
      await service.cleanup();
      console.log(`🧹 Cleaned up WebSocket service: ${serviceName}`);
    } catch (error) {
      console.error(`❌ Error cleaning up service ${serviceName}:`, error);
    }
    
    this.services.delete(serviceName);
    console.log(`🗑️ Unregistered WebSocket service: ${serviceName}`);
  }
  
  // Initialize all registered services
  async initializeServices(router: WebSocketMessageRouter, dispatch: AppDispatch): Promise<void> {
    if (this.initialized) {
      console.warn('⚠️ Service registry already initialized');
      return;
    }
    
    if (!this.router) {
      this.router = router;
    }
    
    console.log(`🚀 Initializing WebSocket service registry with ${this.services.size} services...`);
    
    // Sort services by priority for initialization
    const servicesArray = Array.from(this.services.values())
      .sort((a, b) => a.getHandlerPriority() - b.getHandlerPriority());
    
    // Initialize each service
    for (const service of servicesArray) {
      try {
        console.log(`🔄 Initializing service: ${service.name}`);
        await service.initialize(router, dispatch);
        console.log(`✅ Initialized WebSocket service: ${service.name}`);
      } catch (error) {
        console.error(`❌ Failed to initialize service ${service.name}:`, error);
        // Continue with other services even if one fails
      }
    }
    
    this.initialized = true;
    
    // Log final statistics
    const stats = this.getStatistics();
    console.log(`🎯 WebSocket service registry initialized:`, stats);
  }
  
  // Cleanup all services
  async cleanupServices(): Promise<void> {
    if (!this.initialized) {
      return;
    }
    
    console.log(`🧹 Cleaning up WebSocket service registry...`);
    
    const cleanupPromises = Array.from(this.services.values()).map(async service => {
      try {
        await service.cleanup();
        console.log(`🧹 Cleaned up service: ${service.name}`);
      } catch (error) {
        console.error(`❌ Error cleaning up service ${service.name}:`, error);
      }
    });
    
    await Promise.allSettled(cleanupPromises);
    
    this.services.clear();
    this.handlers.clear();
    this.initialized = false;
    this.router = null;
    
    console.log(`✅ WebSocket service registry cleanup complete`);
  }
  
  // Get handlers for a message type
  getHandlers(messageType: string): MessageHandler[] {
    return this.handlers.get(messageType) || [];
  }
  
  // Get all registered services
  getServices(): IWebSocketService[] {
    return Array.from(this.services.values());
  }
  
  // Get service by name
  getService(name: string): IWebSocketService | undefined {
    return this.services.get(name);
  }
  
  // Check if service is registered
  hasService(name: string): boolean {
    return this.services.has(name);
  }
  
  // Check service dependencies
  private async checkDependencies(service: IWebSocketService): Promise<void> {
    if (!service.dependencies) return;
    
    for (const dependency of service.dependencies) {
      if (!this.services.has(dependency)) {
        throw new Error(`Service ${service.name} depends on ${dependency} which is not registered`);
      }
    }
    
    console.log(`✅ Dependencies validated for service ${service.name}: [${service.dependencies.join(', ')}]`);
  }
  
  // Get service initialization order (dependency-aware)
  getServiceInitializationOrder(): string[] {
    const order: string[] = [];
    const visiting = new Set<string>();
    const visited = new Set<string>();
    
    const visit = (serviceName: string) => {
      if (visited.has(serviceName)) return;
      if (visiting.has(serviceName)) {
        throw new Error(`Circular dependency detected involving service: ${serviceName}`);
      }
      
      visiting.add(serviceName);
      const service = this.services.get(serviceName);
      
      if (service?.dependencies) {
        for (const dep of service.dependencies) {
          if (this.services.has(dep)) {
            visit(dep);
          }
        }
      }
      
      visiting.delete(serviceName);
      visited.add(serviceName);
      order.push(serviceName);
    };
    
    for (const service of this.services.values()) {
      visit(service.name);
    }
    
    return order;
  }
  
  // Get statistics
  getStatistics() {
    const serviceInfos = Array.from(this.services.values()).map(service => ({
      name: service.name,
      version: service.version,
      messageTypes: service.getMessageTypes(),
      priority: service.getHandlerPriority()
    }));
    
    return {
      totalServices: this.services.size,
      initialized: this.initialized,
      registeredMessageTypes: this.handlers.size,
      totalHandlers: Array.from(this.handlers.values()).reduce((sum, handlers) => sum + handlers.length, 0),
      services: serviceInfos,
      initializationOrder: this.getServiceInitializationOrder()
    };
  }
}

// Global registry instance
export const serviceRegistry = new WebSocketServiceRegistry();